import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import { FormattedMessage } from 'dibs-react-intl';
import { executeCaptcha, recaptchaV3 } from 'dibs-recaptcha/exports/recaptcha';
import get from 'lodash.get';
import pick from 'lodash.pick';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
    createFragmentContainer,
    fetchQuery_DEPRECATED as fetchQuery,
    graphql,
} from 'react-relay/legacy';
import serverVars from 'server-vars';
import { addCreditCard } from '../actions/checkoutPaymentActions';
import {
    addAddress,
    addressDeleted,
    addressFormCanceled,
    addressFormUpdated,
    addressSaved,
    addressUpdated,
    editAddress,
    embeddedRegistrationSaved,
    embeddedRegistrationSavedError,
    setShowTaxWarning,
} from '../actions/checkoutShippingActions';
import { addressSelectedAction, shippingCompletedAction } from '../actions/commonActions';
import {
    finishAddressSaveAction,
    setWasNavigatedDueTaxWarningAction,
    startAddressSaveAction,
    setTriggeredErrors,
} from '../actions/checkoutUIActions';
import {
    CHECKOUT_REGISTRATION_TRIGGER,
    METHODS,
    STATES,
    STEPS,
} from '../constants/checkoutConstants';
import {
    getAddressErrors,
    getErrorMessages,
    getTrimmedAddressFields,
    isSameAddresses,
    shippingFields,
} from '../helpers/checkoutForms';
import {
    trackAuthModalLogin,
    trackAuthModalRegistration,
    trackCompletedStep,
    trackFormError,
    trackFormErrors,
    trackLogin,
} from '../helpers/checkoutTracking';
import { getBuyerId } from 'dibs-cookie-jar';
import { getAuthHash, isLoggedIn } from '../helpers/checkoutUser';
import { logCheckoutError } from '../helpers/logCheckoutError';
import { getGuestUserId } from '../helpers/checkoutGuestUserToken';
import { isGuestCheckoutV2Active } from '../helpers/guestCheckoutAbTestHelpers';
import CheckoutMutationWrapper from '../mutations/CheckoutMutationWrapper';
import { canUsePreFilledAddress, isAdding, isEditing } from '../reducers/checkoutShippingReducer';

import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';

// components
import CheckoutConfirmModal from '../shared/CheckoutConfirmModal';
import CheckoutShippingAndDeliveryError from '../components/shared/CheckoutShippingAndDeliveryError';
import CheckoutShippingForm from './CheckoutShippingForm';
import CheckoutEmailOnlyShippingForm from './CheckoutEmailOnlyShippingForm';
import CheckoutGuestShippingForm from './CheckoutGuestShippingForm';
import CheckoutShippingAddressListWrapper from './CheckoutShippingAddressListWrapper';

// modals
import { PriceBookNameChangeModal } from './modals/PriceBookNameChangeModal';

import dibsCss from 'dibs-css';

const getAddressById = (buyer, id) => {
    const address =
        (!!id && (get(buyer, 'addressBook.addressList') || []).find(a => a.serviceId === id)) || {};
    return (!!id && pick(address, [...shippingFields, 'serviceId'])) || null;
};

const getAddressPriceBookName = (buyer, id) => {
    if (!id) {
        return null;
    }

    return (
        (buyer?.addressBook?.addressList || []).find(
            address => address?.serviceId === id.toString()
        )?.priceBookName || 'DEFAULT'
    );
};

const isBuyerAddressValid = (buyer, id) => {
    return (buyer?.addressBook?.addressList || []).find(
        address => address?.serviceId === id.toString()
    )?.validation?.buyerShipping?.valid;
};

const getValidCreditCardToken = (buyer, id) => {
    const walletPaymentMethods =
        !!id &&
        (get(buyer, 'wallet.paymentMethods') || []).filter(
            ({ paymentMethod, shippingAddresses = [] }) =>
                paymentMethod.apiType === METHODS.METHOD_CREDIT_CARD &&
                !paymentMethod.isExpired &&
                shippingAddresses.find(address => address.serviceId === id)
        );
    const validTokens = (walletPaymentMethods || []).reduce(
        (arr, walletPaymentMethod) => [
            ...arr,
            get(walletPaymentMethod, 'paymentMethod.token') || null,
        ],
        []
    );
    return validTokens[0] || null;
};

export const getActionProps = (user, shippingAddressId) => {
    const actionProps = {
        shippingAddress: getAddressById(user, shippingAddressId),
        paymentMethodToken: getValidCreditCardToken(user, shippingAddressId),
    };

    return actionProps;
};

const addressUpdater = (store, data, viewer, buyer, mutationName) => {
    const payload = store.getRootField(mutationName);
    const payloadUserProxy = payload.getLinkedRecord('user');
    const viewerProxy = store.get(viewer.id);
    const buyerProxy = viewerProxy.getOrCreateLinkedRecord('user', 'User', {
        userId: payloadUserProxy.getValue('serviceId'),
    });
    const prevWalletProxy = buyerProxy.getOrCreateLinkedRecord('wallet', 'Wallet');
    buyerProxy.copyFieldsFrom(payloadUserProxy);
    // for guest users wallet will come back null but we dont want to overwrite it.
    buyerProxy.setLinkedRecord(prevWalletProxy, 'wallet');
};

class CheckoutShippingStep extends Component {
    constructor(props) {
        super(props);
        this.onSelect = this.onSelect.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onComplete = this.onComplete.bind(this);
        this.onDischarge = this.onDischarge.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
        this.onClose = this.onClose.bind(this);
        this.state = {
            isOpen: false,
            upcomingState: null,
            upcomingAddressId: null,
            showMutationError: false,
            isLoading: false,
        };
    }
    componentDidMount() {
        const { wasNavigatedDueTaxWarning, showTaxWarning, shippingAddressId } = this.props;
        if (wasNavigatedDueTaxWarning && showTaxWarning) {
            this.onEdit(shippingAddressId);
        }
    }
    hasAddressChanged() {
        const { buyer, shippingAddressId, editShippingForm } = this.props;
        const savedAddress = getAddressById(buyer, shippingAddressId);
        return shippingFields.some(k => savedAddress[k] !== editShippingForm[k]);
    }
    updateAddress(callback = () => null) {
        // update existing address
        const { upcomingAddressId, upcomingState } = this.state;
        const { shippingAddressId, onStartAddressSave, dispatch, viewer, buyer } = this.props;
        const hasAddressChanged = this.hasAddressChanged();
        const buyerId = get(buyer, 'serviceId');

        if (hasAddressChanged && buyerId) {
            const { relay, transaction, editShippingForm, onAddressUpdated, onFinishAddressSave } =
                this.props;
            const { isMobile } = this.context;

            onStartAddressSave();

            CheckoutMutationWrapper.commit(
                'UserEditAddressMutation',
                relay.environment,
                {
                    userId: buyer.serviceId,
                    addressId: shippingAddressId,
                    transactionId: viewer?.isMultiStageCheckoutOn ? transaction?.serviceId : null,
                    address: {
                        ...editShippingForm,
                        ...getTrimmedAddressFields({ address: editShippingForm }),
                    },
                    validateBuyerShipping: true,
                },
                isMobile
            )
                .then(response => {
                    const { user, address } = get(response, 'userEditAddress');
                    const addressId =
                        !!upcomingAddressId && upcomingAddressId !== STATES.STATE_NEW
                            ? upcomingAddressId
                            : address.serviceId;
                    onFinishAddressSave();
                    onAddressUpdated({
                        ...getActionProps(user, addressId),
                        upcomingState,
                        upcomingAddressId,
                        shippingAddressId,
                    });
                    callback();
                })
                .catch(() => {
                    onFinishAddressSave();
                    callback();
                });
        } else {
            dispatch({ type: 'CHECKOUT_SET_SHIPPING_STATE', shippingState: STATES.STATE_SAVED });
        }
    }
    async deleteAddress(callback = () => null) {
        // delete an address
        const { upcomingAddressId } = this.state;
        const { relay, viewer, buyer, shippingAddressId, onAddressDeleted, transaction } =
            this.props;
        const { isMobile } = this.context;

        this.setState({ isLoading: true, showMutationError: false });
        try {
            const response = await CheckoutMutationWrapper.commit(
                'UserDeleteAddressMutation',
                relay.environment,
                {
                    userId: buyer.serviceId,
                    addressId: upcomingAddressId,
                },
                isMobile
            );

            const { user } = get(response, 'userDeleteAddress');
            const isSelected = upcomingAddressId === shippingAddressId;
            const personalAddress = user?.addressBook?.personalAddress;
            const newShippingAddressId = isSelected
                ? personalAddress?.serviceId || user?.firstValidShippingAddress?.serviceId || null
                : null;

            if (!newShippingAddressId) {
                onAddressDeleted();
            } else if (isSelected) {
                onAddressDeleted({
                    ...getActionProps(user, newShippingAddressId),
                    isEditingPartial:
                        !!personalAddress?.serviceId &&
                        !personalAddress?.validation?.buyerShipping?.valid,
                });
            }

            // if the address being deleted is the selected address, we need to update the transaction
            if (viewer?.isMultiStageCheckoutOn && isSelected && newShippingAddressId) {
                await CheckoutMutationWrapper.commit(
                    'TransactionUpdateShipment',
                    relay.environment,
                    {
                        transactionId: transaction.serviceId,
                        shippingAddressId: newShippingAddressId,
                    },
                    isMobile
                );
            }
            this.setState({ isLoading: false });
            callback();
        } catch (error) {
            this.setState({ isLoading: false, showMutationError: true });
            callback();
            logCheckoutError(error, 'shipping');
        }
    }
    async saveAddress(callback = () => null) {
        // save new address
        const { upcomingState, upcomingAddressId } = this.state;
        const {
            relay,
            viewer,
            buyer,
            transaction,
            newShippingForm,
            shippingAddressId,
            onStartAddressSave,
            onFinishAddressSave,
            dispatch,
            offerPriceBookName,
            transactionType,
            onEmbeddedRegistrationSaved,
            isMarketingOptIn,
        } = this.props;
        const { isMobile } = this.context;

        // get buyer id from cookie, it may not be set in relay if coming from guest checkout
        const guestUserId = getGuestUserId();
        const userId = getBuyerId(document.cookie) || guestUserId;

        const isGdprApplicable = get(viewer, 'gdprDisplayInfo.isGdprApplicable');
        const marketingOptIn = !isGdprApplicable || isMarketingOptIn; // automatically opt-in non-GDPR to marketing

        if (userId) {
            onStartAddressSave();

            CheckoutMutationWrapper.commit(
                'UserAddAddressMutation',
                relay.environment,
                {
                    userId: userId,
                    address: {
                        ...newShippingForm,
                        ...getTrimmedAddressFields({ address: newShippingForm }),
                        addressBookMember: !guestUserId,
                        serviceId: undefined,
                    },
                    mockAddressBook: !!guestUserId,
                    // only set name on profile & marketingOptIn when this is a new guest checkout user
                    // buyer will not be populated in that case
                    setNameOnProfile: !buyer,
                    marketingOptIn: buyer ? false : marketingOptIn,
                    isGdprApplicable,

                    transactionId: viewer?.isMultiStageCheckoutOn ? transaction?.serviceId : null,
                    validateBuyerShipping: true,
                },
                isMobile,
                guestUserId
                    ? (store, data) => addressUpdater(store, data, viewer, buyer, 'userAddAddress')
                    : null
            )
                .then(response => {
                    const { user, address } = get(response, 'userAddAddress');
                    onFinishAddressSave();
                    if (buyer) {
                        addressSaved({
                            viewer,
                            transaction,
                            ...getActionProps(user, upcomingAddressId || address.serviceId),
                            upcomingState,
                            upcomingAddressId,
                            shippingAddressId,
                            offerPriceBookName,
                            addressPriceBookName: getAddressPriceBookName(
                                user,
                                upcomingAddressId || address.serviceId
                            ),
                            transactionType,
                            dispatch,
                        });
                    } else {
                        // if coming from guest checkout
                        onEmbeddedRegistrationSaved({
                            buyerId: user.serviceId,
                            address,
                            isNewUser: true,
                        });
                        trackCompletedStep(STEPS.STEP_SHIPPING);
                    }
                })
                .catch(() => {
                    onFinishAddressSave();
                    callback();
                });
        }
    }
    async saveEmbeddedRegistration() {
        // save new address with embedded registration
        const {
            relay,
            viewer,
            newShippingForm,
            registrationForm,
            offerCurrency,
            isMarketingOptIn,
            onEmbeddedRegistrationSavedError,
            onTriggeredErrors,
            onEmbeddedRegistrationSaved,
            onStartAddressSave,
            buyer,
        } = this.props;
        const { email, password } = registrationForm || {};
        const wasEmailOnlyUser = get(buyer, 'authMetadata.emailOnly');
        const isGdprApplicable = get(viewer, 'gdprDisplayInfo.isGdprApplicable');
        const marketingOptIn = !isGdprApplicable || isMarketingOptIn; // automatically opt-in non-GDPR to marketing
        onStartAddressSave();

        const body = {
            email,
            password,
            isGdprApplicable,
            marketingOptIn,
            address: newShippingForm,
            currency: offerCurrency,
            authHash: getAuthHash({ email }),
            validateBuyerShipping: true,
            cookieDomain: serverVars.get('cookieDomain'), // for setting response cookies
            registrationTrigger: CHECKOUT_REGISTRATION_TRIGGER,
        };

        const {
            viewer: { isLoginCaptchaEnabled, isLoginCaptchaV3Enabled, isRegisterCaptchaEnabled },
        } = await fetchQuery(
            relay.environment,
            graphql`
                query CheckoutShippingStepQuery {
                    viewer {
                        isLoginCaptchaEnabled: featureFlag(feature: "loginCaptcha")
                        isLoginCaptchaV3Enabled: featureFlag(feature: "loginCaptchaV3")
                        isRegisterCaptchaEnabled: featureFlag(feature: "registerCaptcha")
                    }
                }
            `
        );

        //Check for both since graph-ql might do login or register calls, and we cant know here which
        if (isLoginCaptchaV3Enabled) {
            const { captchaToken, captchaKey } = await recaptchaV3({
                action: 'checkoutRegister',
            });
            body.captchaToken = captchaToken;
            body.captchaKey = captchaKey;
        } else if (isLoginCaptchaEnabled || isRegisterCaptchaEnabled) {
            const { captchaToken, captchaKey } = await executeCaptcha({
                action: 'checkoutRegister',
            });
            body.captchaToken = captchaToken;
            body.captchaKey = captchaKey;
        }

        // Requires auth hash to prevent bots hitting graphQL directly and making fake auths.
        CheckoutMutationWrapper.commit('UserRegisterAndAddAddressMutation', relay.environment, body)
            .then(response => {
                const errMessage = get(response, 'userRegisterAndAddAddress.errorMessage');
                if (errMessage) {
                    onTriggeredErrors([errMessage]);
                    return onEmbeddedRegistrationSavedError(errMessage);
                }
                const { user, address, isNewUser } = get(response, 'userRegisterAndAddAddress');

                if (isNewUser) {
                    trackLogin({ label: 'registration step 1 completed (email)' });
                    trackLogin({ label: 'registration step 2 completed (password)' });
                    trackAuthModalRegistration();
                } else {
                    trackAuthModalLogin();
                }
                trackCompletedStep(STEPS.STEP_SHIPPING);
                return onEmbeddedRegistrationSaved({
                    buyerId: user.serviceId,
                    address,
                    isNewUser: isNewUser || wasEmailOnlyUser,
                });
            })
            .catch(e => {
                logCheckoutError(e, 'shipping');
                const errMessage = 'Server Error';
                onTriggeredErrors([errMessage]);
                onEmbeddedRegistrationSavedError(errMessage);
            });
    }
    async onSelect(event, addressId, isSelected) {
        // trigger on address selection
        if (isSelected) {
            return;
        }
        const {
            buyer,
            isAddingShipping,
            isEditingShipping,
            newShippingForm,
            editShippingForm,
            onAddShippingAddress,
            onSelectAddress,
            onSetWasNavigatedDueTaxWarning,
            onSetShowTaxWarning,
        } = this.props;
        const addressForm = isEditingShipping ? editShippingForm : newShippingForm;
        const isSelectedAddressValid = isBuyerAddressValid(buyer, addressId);
        const isValid =
            (isAddingShipping || isEditingShipping) && getAddressErrors(addressForm).isValid;
        const hasUnsavedAddress = isValid && this.hasAddressChanged();
        event.preventDefault();
        event.stopPropagation();

        onSetWasNavigatedDueTaxWarning(false);
        onSetShowTaxWarning(false);

        if (hasUnsavedAddress) {
            this.setState({
                isOpen: true,
                upcomingState: STATES.STATE_SAVED,
                upcomingAddressId: addressId,
            });
        } else if (addressId === STATES.STATE_NEW) {
            onAddShippingAddress();
        } else if (!isSelectedAddressValid) {
            this.onEdit(addressId, true);
        } else {
            onSelectAddress({
                shippingState: STATES.STATE_SAVED,
                ...getActionProps(buyer, addressId),
            });
        }
    }
    onChange({ field, value }) {
        // trigger on form field change
        const { onAddressFormUpdated, isAddingShipping } = this.props;
        if (field) {
            onAddressFormUpdated({
                field,
                value,
                isAdding: isAddingShipping,
            });
        }
    }
    onBlur({ field, error }) {
        // trigger on form field blur
        if (error) {
            trackFormError({ step: STEPS.STEP_PAYMENT, field, error });
            this.props.onTriggeredErrors([field]);
        }
    }
    onCancel() {
        const {
            canUsePreFilledShippingAddress,
            isAddingShipping,
            lastValidShippingAddressId,
            onAddressFormCanceled,
            onSetShowTaxWarning,
            isSelectedAddressValid,
            shippingAddressId,
            buyer,
        } = this.props;
        onSetShowTaxWarning(false);
        // trigger on form cancellation
        const actionProps = getActionProps(buyer, shippingAddressId);
        onAddressFormCanceled({
            canUsePreFilledShippingAddress,
            isAddingShipping,
            lastValidShippingAddressId,
            isSelectedAddressValid,
            ...actionProps,
        });
        this.setState({
            upcomingState: isSelectedAddressValid ? STATES.STATE_SAVED : STATES.STATE_EDIT,
        });
    }
    onEdit(addressId, isEditingPartial = false) {
        // trigger on edit action
        const {
            buyer,
            shippingAddressId,
            isAddingShipping,
            isEditingShipping,
            newShippingForm,
            editShippingForm,
            onEditAddress,
        } = this.props;

        let openConfirmationModal = false;

        if (isAddingShipping || isEditingShipping) {
            const addressForm = isAddingShipping ? newShippingForm : editShippingForm;
            const selectedAddress = (get(buyer, 'addressBook.addressList') || []).find(
                a => a.serviceId === shippingAddressId
            );
            openConfirmationModal =
                !isSameAddresses(addressForm, selectedAddress) &&
                getAddressErrors(addressForm).isValid;
        }

        if (openConfirmationModal) {
            this.setState({
                isOpen: true,
                upcomingState: STATES.STATE_EDIT,
                upcomingAddressId: addressId,
            });
        } else {
            const actionProps = getActionProps(buyer, addressId);
            onEditAddress({
                ...actionProps,
                isEditingPartial,
            });
        }
    }
    onDelete(addressId) {
        // trigger on delete action
        const { dispatch } = this.props;

        this.setState({
            isOpen: true,
            upcomingAddressId: addressId,
        });

        //Reset shipping state as saved if "Delete" was triggered through the Edit Shipping Form (I.E., current address is only partial address)
        //Resetting state shortcircuits the "Edit" process and allows user to continue through the "Delete" process

        dispatch({ type: 'CHECKOUT_SET_SHIPPING_STATE', shippingState: STATES.STATE_SAVED });
    }
    onSubmit({ isValid, errors }) {
        // trigger on form submission
        if (isValid) {
            const { isAddingShipping, shippingAddressId, onSetShowTaxWarning } = this.props;
            onSetShowTaxWarning(false);
            if (
                (!isLoggedIn() || isEmailOnly(document.cookie)) &&
                !(this.isGuestCheckout() || getGuestUserId())
            ) {
                this.saveEmbeddedRegistration();
            } else if (isAddingShipping) {
                this.saveAddress();
            } else if (shippingAddressId) {
                this.updateAddress();
            }
        } else {
            trackFormErrors({
                step: STEPS.STEP_SHIPPING,
                errors: getErrorMessages(errors),
            });
            if (errors && Object.keys(errors).length) {
                this.props.onTriggeredErrors(Object.keys(errors));
            }
        }
    }
    onComplete(event) {
        // trigger on continue to other form sections
        const {
            viewer,
            transaction,
            isComputing,
            shippingAddressId,
            onCompleteShippingStep,
            buyer,
            onAddCreditCard,
        } = this.props;
        event.preventDefault();

        if (!isComputing) {
            onCompleteShippingStep({
                viewer,
                transaction,
                shippingAddressId,
            });
            if (!buyer.hasCreditCards) {
                onAddCreditCard();
            }
        }
    }
    onDischarge() {
        // trigger on confirmation modal discharge
        const { upcomingAddressId, upcomingState } = this.state;
        const { onAddShippingAddress, onSelectAddress, buyer } = this.props;
        const isSelectedAddressValid = isBuyerAddressValid(buyer, upcomingAddressId);

        if (upcomingAddressId === STATES.STATE_NEW) {
            onAddShippingAddress();
        } else if (!isSelectedAddressValid) {
            this.onEdit(upcomingAddressId, true);
        } else {
            onSelectAddress({
                shippingState: upcomingState,
                ...getActionProps(buyer, upcomingAddressId),
            });
        }

        this.onClose();
    }
    onConfirm() {
        // trigger on confirmation modal submission
        const { isAddingShipping, isEditingShipping } = this.props;
        const callback = () => this.onClose();

        if (isAddingShipping) {
            this.saveAddress(callback);
        } else if (isEditingShipping) {
            this.updateAddress(callback);
        } else {
            this.deleteAddress(callback);
        }
    }
    onClose() {
        this.setState({
            isOpen: false,
            upcomingState: null,
            upcomingAddressId: null,
        });
    }
    isGuestCheckout() {
        return this.props.isEmailOnlyCheckout;
    }
    render() {
        const { isOpen, isLoading, showMutationError } = this.state;
        const isGuestUser = !!getGuestUserId();
        const { isMobile } = this.context;
        const { viewer, transaction, buyer, isComputing, offerPriceBookName, shippingAddressId } =
            this.props;
        const hasAddresses = !!get(buyer, 'firstValidShippingAddress.serviceId');
        const addressPriceBookName = getAddressPriceBookName(buyer, shippingAddressId);
        const actions = {
            onEdit: this.onEdit,
            onDelete: this.onDelete,
            onCancel: this.onCancel,
        };

        return (
            <Fragment>
                {showMutationError && <CheckoutShippingAndDeliveryError viewer={viewer} />}
                <PriceBookNameChangeModal
                    buyer={buyer}
                    transaction={transaction}
                    offerPriceBookName={offerPriceBookName}
                    addressPriceBookName={addressPriceBookName}
                />
                {!isMobile && !isGuestCheckoutV2Active() && (
                    <div className={`${dibsCss.sassyFontBodySizeSmall} ${dibsCss.ptXsmall}`}>
                        <FormattedMessage
                            key="shippingShippingDisclaimer"
                            id="checkout.CheckoutShippingStep.disclaimer"
                            defaultMessage="Provide your address to see available shipping methods below."
                        />
                    </div>
                )}
                {hasAddresses && !isGuestUser ? (
                    <CheckoutShippingAddressListWrapper
                        viewer={viewer}
                        buyer={buyer}
                        actions={actions}
                        isComputing={isComputing || isLoading}
                        onSelect={this.onSelect}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onSubmit={this.onSubmit}
                        onComplete={this.onComplete}
                    />
                ) : (
                    <>
                        {this.isGuestCheckout() ? (
                            <>
                                {isGuestCheckoutV2Active() ? (
                                    <CheckoutGuestShippingForm
                                        viewer={viewer}
                                        isComputing={isComputing || isLoading}
                                        onChange={this.onChange}
                                        onBlur={this.onBlur}
                                        onSubmit={this.onSubmit}
                                    />
                                ) : (
                                    <CheckoutEmailOnlyShippingForm
                                        viewer={viewer}
                                        isComputing={isComputing || isLoading}
                                        onChange={this.onChange}
                                        onBlur={this.onBlur}
                                        onSubmit={this.onSubmit}
                                    />
                                )}
                            </>
                        ) : (
                            <CheckoutShippingForm
                                viewer={viewer}
                                buyer={buyer}
                                isComputing={isComputing || isLoading}
                                onChange={this.onChange}
                                onBlur={this.onBlur}
                                onSubmit={this.onSubmit}
                            />
                        )}
                    </>
                )}
                <CheckoutConfirmModal
                    isOpen={isOpen}
                    onDischarge={this.onDischarge}
                    onConfirm={this.onConfirm}
                    onClose={this.onClose}
                />
            </Fragment>
        );
    }
}

CheckoutShippingStep.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object,
    transaction: PropTypes.object,
    transactionId: PropTypes.string,
    buyerId: PropTypes.string,
    isEmailOnlyCheckout: PropTypes.bool.isRequired,
    isComputing: PropTypes.bool.isRequired,
    isAddingShipping: PropTypes.bool.isRequired,
    isEditingShipping: PropTypes.bool.isRequired,
    isMarketingOptIn: PropTypes.bool.isRequired,
    newShippingForm: PropTypes.object.isRequired,
    editShippingForm: PropTypes.object.isRequired,
    registrationForm: PropTypes.object.isRequired,
    offerCurrency: PropTypes.string.isRequired,
    shippingAddressId: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    onAddShippingAddress: PropTypes.func.isRequired,
    onAddressFormUpdated: PropTypes.func.isRequired,
    onEmbeddedRegistrationSavedError: PropTypes.func.isRequired,
    onEmbeddedRegistrationSaved: PropTypes.func.isRequired,
    onStartAddressSave: PropTypes.func.isRequired,
    onFinishAddressSave: PropTypes.func.isRequired,
    canUsePreFilledShippingAddress: PropTypes.bool.isRequired,
    lastValidShippingAddressId: PropTypes.string,
    onSelectAddress: PropTypes.func.isRequired,
    onEditAddress: PropTypes.func.isRequired,
    onAddressFormCanceled: PropTypes.func.isRequired,
    onAddressDeleted: PropTypes.func.isRequired,
    onAddressUpdated: PropTypes.func.isRequired,
    onCompleteShippingStep: PropTypes.func.isRequired,
    wasNavigatedDueTaxWarning: PropTypes.bool,
    showTaxWarning: PropTypes.bool,
    onSetWasNavigatedDueTaxWarning: PropTypes.func,
    onSetShowTaxWarning: PropTypes.func,
    onAddCreditCard: PropTypes.func,
    shipmentType: PropTypes.oneOf(['FIRSTDIBS_FACILITATED', 'LOCAL_PICKUP', 'EXTERNALLY_ARRANGED']),
    isSelectedAddressValid: PropTypes.bool,
    offerPriceBookName: PropTypes.string,
    transactionType: PropTypes.string,
    onTriggeredErrors: PropTypes.func.isRequired,
};

CheckoutShippingStep.contextType = CheckoutServerVarsContext;

const ConnectedCheckoutShippingStep = connect(
    ({ shipping, ui, transaction }) => ({
        isAddingShipping: isAdding(shipping),
        isEditingShipping: isEditing(shipping),
        canUsePreFilledShippingAddress: canUsePreFilledAddress(shipping),
        lastValidShippingAddressId: shipping.lastValidShippingAddressId,
        newShippingForm: shipping.newShippingForm,
        editShippingForm: shipping.editShippingForm,
        registrationForm: shipping.registrationForm,
        isMarketingOptIn: ui.isMarketingOptIn,
        offerCurrency: transaction.offerCurrency,
        shippingAddressId: transaction.shippingAddressId,
        wasNavigatedDueTaxWarning: ui.wasNavigatedDueTaxWarning,
        showTaxWarning: shipping.showTaxWarning,
        shipmentType: transaction.shipmentType,
        isSelectedAddressValid: shipping.isSelectedAddressValid,
        offerPriceBookName: transaction.offerPriceBookName,
        transactionType: transaction.transactionType,
        isGuestCheckout: transaction.isGuestCheckout,
        buyerId: transaction.buyerId,
    }),
    dispatch => ({
        onAddShippingAddress: (...args) => dispatch(addAddress(...args)),
        onEmbeddedRegistrationSavedError: (...args) =>
            dispatch(embeddedRegistrationSavedError(...args)),
        onEmbeddedRegistrationSaved: (...args) => dispatch(embeddedRegistrationSaved(...args)),
        onAddressFormUpdated: payload => dispatch(addressFormUpdated(payload)),
        onSelectAddress: (...args) => dispatch(addressSelectedAction(...args)),
        onEditAddress: (...args) => dispatch(editAddress(...args)),
        onAddressFormCanceled: payload => addressFormCanceled({ dispatch, ...payload }),
        onCompleteShippingStep: payload => dispatch(shippingCompletedAction(payload)),
        onAddressUpdated: payload => dispatch(addressUpdated(payload)),
        onAddressDeleted: payload => dispatch(addressDeleted(payload)),
        onFinishAddressSave: payload => dispatch(finishAddressSaveAction(payload)),
        onStartAddressSave: payload => dispatch(startAddressSaveAction(payload)),
        onSetWasNavigatedDueTaxWarning: payload =>
            dispatch(setWasNavigatedDueTaxWarningAction(payload)),
        onSetShowTaxWarning: payload => dispatch(setShowTaxWarning(payload)),
        onAddCreditCard: payload => dispatch(addCreditCard(payload)),
        onTriggeredErrors: payload => dispatch(setTriggeredErrors(payload)),
        dispatch,
    })
)(CheckoutShippingStep);

export default createFragmentContainer(ConnectedCheckoutShippingStep, {
    viewer: graphql`
        fragment CheckoutShippingStep_viewer on Viewer {
            ...checkoutShippingActions_viewer @relay(mask: false)
            ...CheckoutShippingAddressListWrapper_viewer
            ...CheckoutShippingForm_viewer
            ...CheckoutEmailOnlyShippingForm_viewer
            ...CheckoutGuestShippingForm_viewer
            id
            gdprDisplayInfo {
                isGdprApplicable(countryCode: $userCountryCode)
            }
            isMultiStageCheckoutOn: featureFlag(feature: "multiStageCheckout")
        }
    `,
    buyer: graphql`
        fragment CheckoutShippingStep_buyer on User {
            ...CheckoutShippingAddressListWrapper_buyer
            ...CheckoutShippingForm_buyer
            id
            serviceId
            firstValidShippingAddress {
                serviceId
            }
            hasShippingAddresses
            addressBook {
                addressList {
                    serviceId
                    firstName
                    lastName
                    company
                    line1
                    line2
                    country
                    state
                    city
                    zipCode
                    phoneNumber
                    validation {
                        buyerShipping {
                            valid
                        }
                    }
                    priceBookName
                }
            }
            wallet {
                paymentMethods {
                    paymentMethod {
                        __typename
                        ... on CreditCard {
                            apiType
                            token
                            isExpired
                        }
                    }
                    shippingAddresses {
                        serviceId
                    }
                }
            }
            authMetadata {
                emailOnly
            }
            hasCreditCards
            profile {
                email
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutShippingStep_transaction on Transaction {
            serviceId
            ...PriceBookNameChangeModal_transaction
            ...checkoutShippingActions_transaction @relay(mask: false)
        }
    `,
});
